import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import Layout from '../../components/Layout';
import { authAxios } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

export default function AdminEpisodeCalendar() {
  const { currentSeason, API } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [episodes, setEpisodes] = useState([]);
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  
  // Edit inline states
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editTimezone, setEditTimezone] = useState('America/Denver');
  
  // Generate schedule state
  const [firstEpisodeDate, setFirstEpisodeDate] = useState('');
  const [firstEpisodeTime, setFirstEpisodeTime] = useState('20:00');
  const [generateTimezone, setGenerateTimezone] = useState('America/New_York');
  const [weeklyInterval, setWeeklyInterval] = useState(7);
  
  // Common US timezones
  const timezones = [
    { value: 'America/New_York', label: 'Eastern (ET)' },
    { value: 'America/Chicago', label: 'Central (CT)' },
    { value: 'America/Denver', label: 'Mountain (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
    { value: 'America/Anchorage', label: 'Alaska (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
    { value: 'UTC', label: 'UTC' },
  ];

  useEffect(() => {
    if (currentSeason) {
      loadCalendar();
    }
  }, [currentSeason]);

  const loadCalendar = async () => {
    if (!currentSeason) return;
    
    setLoading(true);
    try {
      const res = await authAxios.get(`${API}/seasons/${currentSeason.id}/episodes`, {
        withCredentials: true
      });
      
      // Ensure we have an array
      const episodesData = res.data || [];
      if (Array.isArray(episodesData)) {
        setEpisodes(episodesData);
      } else {
        console.error('Invalid episodes data:', episodesData);
        setEpisodes([]);
        toast.error('Failed to load episodes - invalid data format');
      }
    } catch (error) {
      console.error('Failed to load calendar:', error);
      setEpisodes([]);
      
      // Better error message
      if (error.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Failed to load episode calendar');
      }
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (episode) => {
    setEditingEpisode(episode.id);
    
    if (episode.air_date) {
      // Don't use Date object - just parse the ISO string directly to avoid timezone conversion
      const [datePart, timePart] = episode.air_date.split('T');
      const timeOnly = timePart ? timePart.substring(0, 5) : '20:00';
      
      setEditDate(datePart);
      setEditTime(timeOnly);
    } else {
      setEditDate('');
      setEditTime('20:00');
    }
    
    setEditTimezone(episode.timezone || 'America/Denver');
  };

  const cancelEditing = () => {
    setEditingEpisode(null);
    setEditDate('');
    setEditTime('');
    setEditTimezone('America/Denver');
  };

  const saveEpisode = async (episodeId) => {
    if (!editDate || !editTime) {
      toast.error('Please fill in date and time');
      return;
    }

    setLoading(true);
    try {
      const combinedDateTime = `${editDate}T${editTime}:00`;
      
      await authAxios.patch(
        `${API}/episodes/${episodeId}`,
        {
          air_date: combinedDateTime,
          timezone: editTimezone
        }
      );

      toast.success('Episode updated');
      setEditingEpisode(null);
      loadCalendar();
    } catch (error) {
      console.error('Failed to update episode:', error);
      toast.error('Failed to update episode');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSchedule = async () => {
    if (!firstEpisodeDate || !firstEpisodeTime) {
      toast.error('Please fill in the first episode date and time');
      return;
    }

    setLoading(true);
    try {
      const response = await authAxios.post(
        `${API}/seasons/${currentSeason.id}/calendar/generate`,
        {
          start_date: firstEpisodeDate,
          air_time: firstEpisodeTime,
          timezone: generateTimezone,
          interval_days: weeklyInterval
        }
      );

      toast.success(response.data.message || 'Schedule generated successfully');
      setShowGenerateDialog(false);
      setFirstEpisodeDate('');
      setFirstEpisodeTime('20:00');
      setGenerateTimezone('America/New_York');
      loadCalendar();
    } catch (error) {
      console.error('Failed to generate schedule:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to generate schedule';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (isoString, tz) => {
    if (!isoString) return '';
    
    try {
      // Air dates are stored as naive wall-clock time in the season's timezone
      // (e.g. "2026-09-23T20:00:00" means 8:00 PM in `tz`), so format the parts
      // directly instead of letting the browser reinterpret them as local time.
      const naive = isoString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
      const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/.test(isoString);
      if (naive && !hasOffset) {
        const [, , month, day, hour, minute] = naive;
        const year = naive[1];
        const h = parseInt(hour, 10);
        const hour12 = h % 12 === 0 ? 12 : h % 12;
        const suffix = h < 12 ? 'AM' : 'PM';
        return `${parseInt(month, 10)}/${parseInt(day, 10)}/${year}, ${hour12}:${minute} ${suffix}`;
      }
      
      const dt = new Date(isoString);
      return dt.toLocaleString('en-US', {
        timeZone: tz || 'America/Denver',
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return isoString;
    }
  };

  const getTimezones = () => [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'Pacific/Honolulu',
    'America/Anchorage',
    'UTC'
  ];

  if (!currentSeason) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto container-mobile">
          <p className="text-slate-600">Please select a season</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto container-mobile">
        {/* Header */}
        <div className="spacing-section flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="heading-hero text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Episode Calendar
            </h1>
            <p className="text-responsive text-slate-600">Season {currentSeason?.number} Schedule</p>
          </div>
          
          <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
            <DialogTrigger asChild>
              <Button className="w-full md:w-auto">Generate Full Season</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Generate Full Season Schedule</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Set the first episode air date, and we'll automatically schedule the rest of the season.
                </p>
                <div>
                  <Label className="text-sm mb-2 block">First Episode Date</Label>
                  <Input
                    type="date"
                    value={firstEpisodeDate}
                    onChange={(e) => setFirstEpisodeDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-sm mb-2 block">Air Time</Label>
                  <Input
                    type="time"
                    value={firstEpisodeTime}
                    onChange={(e) => setFirstEpisodeTime(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-sm mb-2 block">Time Zone</Label>
                  <Select value={generateTimezone} onValueChange={setGenerateTimezone}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {timezones.map(tz => (
                        <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Season timezone for notifications</p>
                </div>
                <div>
                  <Label className="text-sm mb-2 block">Weekly Interval (days)</Label>
                  <Input
                    type="number"
                    value={weeklyInterval}
                    onChange={(e) => setWeeklyInterval(parseInt(e.target.value))}
                    min={1}
                    max={30}
                  />
                  <p className="text-xs text-slate-500 mt-1">Usually 7 days for weekly episodes</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
                <Button onClick={handleGenerateSchedule} disabled={loading}>
                  {loading ? 'Generating...' : 'Generate'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Table View */}
        {loading && !episodes.length ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          </div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Episode</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Air Date & Time</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Timezone</th>
                      <th className="text-center px-4 py-3 text-sm font-semibold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!Array.isArray(episodes) || episodes.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-12 text-slate-600">
                          No episodes found. Click "Generate Full Season" to create the schedule.
                        </td>
                      </tr>
                    ) : (
                      episodes.map((episode) => {
                        if (!episode || !episode.id) return null;
                        
                        return (
                          <tr key={episode.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-4 py-3">
                              <span className="font-medium text-slate-800">Episode {episode.number}</span>
                            </td>
                            <td className="px-4 py-3">
                              {editingEpisode === episode.id ? (
                                <div className="flex gap-1.5">
                                  <Input
                                    type="date"
                                    value={editDate}
                                    onChange={(e) => setEditDate(e.target.value)}
                                    className="text-xs h-8 w-40"
                                  />
                                  <Input
                                    type="time"
                                    value={editTime}
                                    onChange={(e) => setEditTime(e.target.value)}
                                    className="text-xs h-8 w-24"
                                  />
                                </div>
                              ) : (
                                <span className="text-sm text-slate-600">
                                  {episode.air_date ? formatDateTime(episode.air_date, episode.timezone) : 'Not scheduled'}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {editingEpisode === episode.id ? (
                                <Select value={editTimezone} onValueChange={setEditTimezone}>
                                  <SelectTrigger className="text-xs h-8 w-36">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {getTimezones().map((tz) => (
                                      <SelectItem key={tz} value={tz}>{tz.replace('America/', '')}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className="text-xs text-slate-500">
                                  {episode.timezone ? episode.timezone.replace('America/', '') : 'Denver'}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {editingEpisode === episode.id ? (
                                <div className="flex justify-center gap-1">
                                  <Button 
                                    size="sm" 
                                    onClick={() => saveEpisode(episode.id)} 
                                    disabled={loading}
                                    className="h-8 w-8 p-0"
                                    title="Save"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    onClick={cancelEditing}
                                    className="h-8 w-8 p-0"
                                    title="Cancel"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </Button>
                                </div>
                              ) : (
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => startEditing(episode)}
                                  className="h-8 w-8 p-0"
                                  title="Edit"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}

